"use client";

import { motion } from "motion/react";
import {
  Legend,
  LegendItem,
  LegendLabel,
  LegendMarker,
  LegendValue,
} from "@/components/charts/legend";
import {
  useHoverSelect,
  useLegendHoverSelect,
  useSelectionGroup,
} from "@/lib/hooks/use-hover-select";
import { SwipeScroll } from "@/components/charts/swipe-scroll";
import { scrollIntoViewX } from "@/lib/scroll-into-view-x";
import { cn } from "@/lib/utils";
import { useHoverCapable } from "@/lib/hooks/use-hover-capable";
import { DOTTED_MAP_COUNTRY_CODES } from "@/types/modules";
import type { ModuleCategory } from "@/types/modules";
import { stripInlineMarkdown } from "@/utils/inline-markdown";
import DottedMap from "dotted-map";
import { JSX, useEffect, useMemo, useRef, useState } from "react";
import { importsTokenScope } from "./imports-token-scope";
import {
  legendIndexOfCategory,
  resolveCategoryColor,
} from "./shared/category-resolve";
import { accentChartColors } from "./module-accent";

const VALID_COUNTRY_CODES = new Set<string>(DOTTED_MAP_COUNTRY_CODES);

type MapPin = {
  lat: number;
  lng: number;
  label?: string;
  color?: string;
  categoryId?: string;
};

type DottedMapData = {
  pins: MapPin[];
  countries?: string[];
  height?: number;
  grid?: "diagonal" | "vertical";
  backgroundColor?: string;
  dotColor?: string;
  pinRadius?: number;
  categories?: ModuleCategory[];
};

const DEFAULT_DATA: DottedMapData = {
  pins: [
    { label: "New York", lat: 40.73061, lng: -73.935242 },
    { label: "Paris", lat: 48.8534, lng: 2.3488 },
    { label: "Tokyo", lat: 35.6762, lng: 139.6503 },
    { label: "São Paulo", lat: -23.5505, lng: -46.6333 },
    { label: "Sydney", lat: -33.8688, lng: 151.2093 },
  ],
};

interface PinPos {
  cx: number;
  cy: number;
  r: number;
}

function extractPinPositions(svg: string, pinColorSet: Set<string>): PinPos[] {
  const positions: PinPos[] = [];
  const circleRegex = /<circle\b([^>]*?)\/>/gi;
  let match;
  while ((match = circleRegex.exec(svg)) !== null) {
    const attrs = match[1];
    if (!attrs) continue;
    const fillMatch = attrs.match(/fill="([^"]+)"/i);
    if (!fillMatch || !pinColorSet.has(fillMatch[1]!.toLowerCase())) continue;
    const cx = Number(attrs.match(/\bcx="([^"]+)"/i)?.[1] ?? 0);
    const cy = Number(attrs.match(/\bcy="([^"]+)"/i)?.[1] ?? 0);
    const r = Number(attrs.match(/\br="([^"]+)"/i)?.[1] ?? 0.5);
    positions.push({ cx, cy, r });
  }
  return positions;
}

function stripPinCircles(svg: string, pinColorSet: Set<string>): string {
  return svg.replace(/<circle\b([^>]*?)\/>/gi, (m, attrs) => {
    if (!attrs) return m;
    const fillMatch = attrs.match(/fill="([^"]+)"/i);
    if (!fillMatch) return m;
    if (pinColorSet.has(fillMatch[1]!.toLowerCase())) return "";
    return m;
  });
}

function extractViewBox(svg: string): {
  viewBox: string;
  w: number;
  h: number;
} {
  const vb = svg.match(/viewBox="([^"]+)"/i)?.[1] ?? "0 0 800 400";
  const [, , wStr, hStr] = vb.split(" ");
  return { viewBox: vb, w: Number(wStr) || 800, h: Number(hStr) || 400 };
}

export default function ModuleDottedMap({
  data = DEFAULT_DATA,
  grayscale,
  accent,
  className,
}: {
  data?: DottedMapData;
  grayscale?: boolean;
  accent?: number;
  className?: string;
}): JSX.Element {
  const [svgBase, setSvgBase] = useState<string | null>(null);
  const [pinPositions, setPinPositions] = useState<PinPos[]>([]);
  const [viewBox, setViewBox] = useState<{ viewBox: string; w: number; h: number } | null>(null);

  const pins = useMemo(() => data.pins ?? [], [data.pins]);
  const categories = data.categories;
  const free = Boolean(categories && categories.length > 0);
  const palette = useMemo(() => accentChartColors(grayscale, accent), [grayscale, accent]);
  const pinColors = useMemo(
    () =>
      pins.map((p, i) => {
        const fallback = palette[i % palette.length] ?? "var(--chart-1)";
        if (p.color) return p.color;
        if (categories && categories.length > 0) {
          return resolveCategoryColor(p.categoryId, categories, fallback);
        }
        return fallback;
      }),
    [pins, palette, categories],
  );

  // The pin legend and the pin itself are one state, so only the two legends
  // are separate selections: picking a category must drop a standing pin and
  // the other way round.
  const selection = useSelectionGroup();
  const pinLegend = useLegendHoverSelect({ group: selection });
  const activeIndex = pinLegend.selected;
  const mapScrollRef = useRef<HTMLDivElement>(null);

  const categoryLegend = useLegendHoverSelect({ group: selection });
  // The pin legend backs the pin selection, and a legend hover lights the
  // matching pin. A free-category map renders the category legend instead, so
  // its pin legend never reports a hover and `effective` is its `selected`.
  const pin = useHoverSelect<number>({
    control: {
      selected: pinLegend.effective,
      select: pinLegend.select,
      setSelected: pinLegend.setSelected,
    },
  });
  const legendActive = pin.effective;
  const dimActive = legendActive !== null;
  const hasHover = useHoverCapable();

  const categoryLegendItems = useMemo(
    () =>
      free
        ? (categories as ModuleCategory[]).map((cat) => ({
            id: cat.id,
            label: cat.label,
            color: cat.color,
            value: pins.filter((p) => p.categoryId === cat.id).length,
          }))
        : [],
    [free, categories, pins],
  );
  const catFilter =
    free && categoryLegend.filter !== null
      ? categoryLegendItems[categoryLegend.filter]?.id ?? null
      : null;
  const categoryLegendSelectedIndex =
    categoryLegend.selected ??
    legendIndexOfCategory(
      categoryLegendItems,
      activeIndex !== null ? pins[activeIndex]?.categoryId ?? null : null,
    );

  // Both legends feed one scroll target: a category row centres the first pin
  // carrying it, a pin legend row centres its pin, and a pin tapped on the map
  // is only pulled in when it sits outside the viewport. Keying the effect on
  // the resolved index keeps it from firing on unrelated renders, which is what
  // makes re-picking the same row scroll again.
  const scrollPinIndex = (() => {
    if (activeIndex !== null) return activeIndex;
    if (categoryLegend.selected === null) return null;
    const catId = categoryLegendItems[categoryLegend.selected]?.id;
    if (!catId) return null;
    const i = pins.findIndex((p) => p.categoryId === catId);
    return i < 0 ? null : i;
  })();
  const scrollAlign =
    activeIndex !== null && pinLegend.selectedOrigin === "element"
      ? "nearest"
      : "center";
  useEffect(() => {
    if (scrollPinIndex === null) return;
    const container = mapScrollRef.current;
    if (!container) return;
    scrollIntoViewX(
      container,
      container.querySelector(`[data-part-index="${scrollPinIndex}"]`),
      { align: scrollAlign, once: false },
    );
  }, [scrollPinIndex, scrollAlign]);

  useEffect(() => {
    try {
      const validCountries = (data.countries ?? []).filter(
        (c): c is string =>
          typeof c === "string" && VALID_COUNTRY_CODES.has(c),
      );

      const map = new DottedMap({
        height: data.height ?? 60,
        grid: data.grid ?? "diagonal",
        ...(validCountries.length > 0 ? { countries: validCountries } : {}),
      });

      pins.forEach((pin, i) => {
        map.addPin({
          lat: pin.lat,
          lng: pin.lng,
          svgOptions: {
            color: pinColors[i]!,
            radius: data.pinRadius ?? 0.55,
          },
        });
      });

      const raw = map.getSVG({
        radius: 0.22,
        color: data.dotColor ?? "#D4D4D8",
        shape: "circle",
        backgroundColor: data.backgroundColor ?? "transparent",
      });

      const pinColorSet = new Set(pinColors.map((c) => c.toLowerCase()));
      const positions = extractPinPositions(raw, pinColorSet);
      const stripped = stripPinCircles(raw, pinColorSet);

      setSvgBase(stripped);
      setViewBox(extractViewBox(raw));
      setPinPositions(positions);
    } catch (err) {
      console.error("[ModuleDottedMap] Failed to render map:", err);
      setSvgBase(null);
    }
  }, [data, pins, pinColors]);

  return (
    <div
      className={cn("flex w-full flex-col gap-3", className)}
      style={importsTokenScope(grayscale, accent)}
    >
      <SwipeScroll ref={mapScrollRef} className="relative w-full">
        {svgBase && viewBox ? (
          <div className="relative flex h-100 w-max">
            <div
              className={cn(
                "h-full [&>svg]:!h-full [&>svg]:!w-auto",
                // Pins live in a sibling svg, so this reaches only the dot field.
                !data.dotColor &&
                  "dark:[&_circle]:fill-[rgba(255,255,255,0.14)]",
              )}
              dangerouslySetInnerHTML={{ __html: svgBase }}
            />
            <svg
              viewBox={viewBox.viewBox}
              className="absolute inset-0 mx-auto h-full w-auto"
              preserveAspectRatio="xMidYMid meet"
            >
              {pinPositions.map((pos, i) => {
                const isHovered =
                  pin.hovered === i || (!free && pinLegend.hovered === i);
                const isSelected = activeIndex === i;
                const catMiss =
                  catFilter !== null && pins[i]?.categoryId !== catFilter;
                const isFaded =
                  (dimActive && !isHovered && !isSelected) || catMiss;
                const showGlow = (isHovered || isSelected) && !catMiss;
                const color = pinColors[i]!;
                return (
                  <motion.g
                    key={i}
                    onMouseEnter={hasHover ? () => pin.setHovered(i) : undefined}
                    onMouseLeave={hasHover ? () => pin.setHovered(null) : undefined}
                    onClick={(e) => pin.select(i, { kind: "point", element: e.currentTarget })}
                    data-module-type="dotted-map"
                    data-part-index={i}
                    data-part-label={pins[i]?.label ?? ""}
                    style={{
                      cursor: "pointer",
                      filter: showGlow ? `drop-shadow(0 0 1px ${color}) drop-shadow(0 0 2px ${color})` : "none",
                      transition: "filter 0.2s ease-in-out",
                    }}
                    animate={{ opacity: isFaded ? 0.4 : 1 }}
                    transition={{ duration: 0.2, ease: "easeInOut" }}
                  >
                    <motion.circle
                      cx={pos.cx}
                      cy={pos.cy}
                      r={pos.r * 2.6}
                      fill="transparent"
                      stroke={color}
                      strokeWidth={0.25}
                      initial={false}
                      animate={{
                        scale: showGlow ? 1 : 0,
                        opacity: showGlow ? 1 : 0,
                      }}
                      transition={{ duration: 0.25, ease: "easeOut" }}
                      style={{ transformOrigin: `${pos.cx}px ${pos.cy}px` }}
                    />
                    <circle cx={pos.cx} cy={pos.cy} r={pos.r} fill={color} />
                  </motion.g>
                );
              })}
            </svg>
          </div>
        ) : (
          <div className="h-100 w-full animate-pulse rounded-md bg-neutral-100 dark:bg-white/[0.06]" />
        )}
      </SwipeScroll>

      <SwipeScroll>
        {free ? (
          <Legend
            items={categoryLegendItems}
            hoveredIndex={categoryLegend.hovered}
            onHoverChange={categoryLegend.setHovered}
            selectedIndex={categoryLegendSelectedIndex}
            onSelect={categoryLegend.select}
            onLabelRef={categoryLegend.setLabelRef}
            className="flex flex-row gap-1"
          >
            <LegendItem className="flex shrink-0 items-center gap-2 px-2 py-1">
              <LegendMarker />
              <LegendLabel className="text-[13px] font-medium text-[var(--fg)]" />
              <LegendValue />
            </LegendItem>
          </Legend>
        ) : (
          <Legend
            items={pins.map((pin, i) => ({
              id: String(i),
              label:
                stripInlineMarkdown(pin.label) ||
                `${pin.lat.toFixed(2)}, ${pin.lng.toFixed(2)}`,
              value: `${pin.lat.toFixed(2)}, ${pin.lng.toFixed(2)}`,
              color: pinColors[i]!,
            }))}
            hoveredIndex={pinLegend.hovered}
            onHoverChange={pinLegend.setHovered}
            selectedIndex={activeIndex}
            onSelect={pinLegend.select}
            onLabelRef={pinLegend.setLabelRef}
            className="flex flex-row gap-1"
          >
            <LegendItem className="flex shrink-0 items-center gap-2 px-2 py-1">
              <LegendMarker />
              <LegendLabel className="text-[13px] font-medium text-[var(--fg)]" />
            </LegendItem>
          </Legend>
        )}
      </SwipeScroll>
    </div>
  );
}
